import { useState, useEffect, Fragment, useContext } from 'react';

import {
  Grid,
  Paper,
  Typography,
  TextField,
  FormControlLabel,
  Switch,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Radio,
  RadioGroup,
  FormControl,
  FormLabel,
  Button,
  InputLabel,
  OutlinedInput,
  InputAdornment
} from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';
import RemoveIcon from '@material-ui/icons/Remove';
import AddIcon from '@material-ui/icons/Add';
import DeleteIcon from '@material-ui/icons/Delete';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import SaveIcon from '@material-ui/icons/Save';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import SearchIcon from '@material-ui/icons/Search';
import { format } from 'date-fns';
import { useHistory, useParams } from 'react-router-dom';
import { useSnackbar } from 'notistack';

import { getSale, updateSale } from '../../services/sales.service';
import { BackdropComponent } from '../commons/backdrop/backdrop.component';
import { getProducts } from '../../services/products.service';
import { getCategories } from '../../services/categories.service';
import { useStyles } from './sales-edit.styles';
import { ProductsContext } from '../../context/products.context';

export const SalesEditComponent = () => {

  const params = useParams();
  const classes = useStyles();
  const history = useHistory();
  const { enqueueSnackbar } = useSnackbar();
  const productsContext = useContext(ProductsContext);

  const [mobile, setMobile] = useState(true);
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [allProducts, setAllProducts] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState();
  const [selectedProduct, setSelectedProduct] = useState();
  const [order, setOrder] = useState();
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  const productsMap = {};

  const fetchAllProducts = async () => {
    if (productsContext.products.length === 0) {
      const productsItems = await getProducts();
      productsContext.handleProducts(productsItems);
      setAllProducts(productsItems);
    } else {
      setAllProducts(productsContext.products);
    }
  }

  const fetchCategories = async () => {
    await fetchAllProducts();
    if (productsContext.categories.length === 0) {
      const categoriesItems = await getCategories();
      productsContext.handleCategories(categoriesItems);
      setCategories(categoriesItems);
      setLoading(false);
    } else {
      setCategories(productsContext.categories);
      setLoading(false);
    }
  }

  const fetchProductsByCategory = (categoryId) => {
    const productsItems = productsContext.getProductsByCategory(categoryId);
    setProducts(productsItems);
  }

  const fetchSale = async (saleId) => {
    const saleItem = await getSale(saleId);
    setOrder(saleItem);
    await fetchCategories();
    setLoading(false);
  }

  useEffect(() => {
    fetchSale(params.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params.id]);

  const addProductToOrder = (productId, price) => {
    if (order.items.some(item => item.product === productId)) {
      increaseProduct(productId, price);
    } else {
      const item = {
        product: productId,
        quantity: 1
      }
      setOrder({
        ...order,
        items: [...order.items, item],
        total: order.total + price
      });
    }
  }

  const getProduct = (productId) => {
    if (productsMap[productId]) {
      return productsMap[productId];
    }
    const product = allProducts.find(p => p.id === productId);
    productsMap[productId] = product;
    return product;
  }

  const increaseProduct = (productId, price) => {
    setOrder({
      ...order,
      items: order.items.map(item => item.product === productId ? {
        product: productId,
        quantity: item.quantity + 1
      } : item),
      total: order.total + price
    })
  }

  const decreaseProduct = (productId, price) => {
    setOrder({
      ...order,
      items: order.items.map(item => item.product === productId ? {
        product: productId,
        quantity: item.quantity - 1
      } : item),
      total: order.total - price
    });
  }

  const handlePaymentChange = (event, value) => {
    setOrder({
      ...order,
      paymentMethod: value
    });
  }

  const deleteProduct = (productId, current, price) => {
    const decreaseQuantity = current * price;
    setOrder({
      ...order,
      items: order.items.filter(item => item.product !== productId),
      total: order.total - decreaseQuantity
    });
  }

  const handleSelectCategory = (category) => {
    setSelectedCategory(category)
    fetchProductsByCategory(category.id);
  }

  const handleChangeTable = (event) => {
    const { value } = event.target;
    setOrder({
      ...order,
      table: value
    });
  }

  const handleSearch = (event) => {
    const { value } = event.target;
    setSearch(value);
  }

  const handleNotesChange = (event) => {
    const { value } = event.target;
    setOrder({
      ...order,
      notes: value
    });
  }

  const handleChangeExtra = (event) => {
    const { value } = event.target;
    setOrder({
      ...order,
      extra: isNaN(+value) ? 0 : +value,
    });
  }

  const save = async (status) => {
    try {
      const saleSave = {
        ...order,
        status
      }
      await updateSale(params.id, saleSave);
      enqueueSnackbar('Venta editada', {
        variant: 'success',
        resumeHideDuration: 3000,
        anchorOrigin:
          { horizontal: 'right', vertical: 'bottom' },
      });
      history.push('/sales');
    } catch (err) {
      enqueueSnackbar(JSON.stringify(err), {
        variant: 'error',
        resumeHideDuration: 6000,
        anchorOrigin:
          { horizontal: 'right', vertical: 'bottom' },
      });
    }
  }

  return loading ? (
    <BackdropComponent />
  ) : (
    <Grid container justifyContent="center">
      <Grid item xs={12} lg={8}>
        <Grid container justifyContent="center">
          <Grid item xs={12} lg={11}>
            <Grid container justifyContent="center">
              <Grid item xs={10}>
                <Typography variant="h3" align="center">
                  Editar venta
                </Typography>
              </Grid>
              <Grid item xs={10}>
                <Typography variant="body1" align="center">
                  {format(new Date(), 'dd/MM/yyyy H:mm')}
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Grid container>
                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={mobile}
                          onChange={() => setMobile(!mobile)}
                          name="mobile"
                          color="primary"
                        />
                      }
                      label="Vista de Celular"
                    />
                  </Grid>
                  {mobile ? (
                    <Grid item xs={12}>
                      <div>
                        <FormControl fullWidth className={classes.margin} variant="outlined">
                          <InputLabel htmlFor="search">Buscar producto</InputLabel>
                          <OutlinedInput
                            id="search"
                            name="search"
                            defaultValue={search}
                            onChange={handleSearch}
                            startAdornment={
                              <InputAdornment position="start">
                                <SearchIcon />
                              </InputAdornment>
                            }
                            labelWidth={120}
                          />
                        </FormControl>
                      </div>
                      {!search && (
                        <div className={classes.scrollable}>
                          <Typography variant="overline" component="p">
                            Categorias
                          </Typography>
                          {categories.sort((a, b) => (a.name > b.name) ? 1 : ((b.name > a.name) ? -1 : 0)).map(category => (
                            <Chip
                              key={category.id}
                              label={category.name}
                              className={classes.chip}
                              onClick={() => fetchProductsByCategory(category.id)}
                            />
                          ))}
                        </div>
                      )}
                      {!search && products.length > 0 && (
                        <div className={classes.scrollable}>
                          <Typography variant="overline" component="p">
                            Productos
                          </Typography>
                          {products.filter(product => product.enable === true).sort((a, b) => (a.name > b.name) ? 1 : ((b.name > a.name) ? -1 : 0)).map(product => (
                            <Chip
                              key={product.id}
                              label={product.name}
                              className={classes.chip}
                              onClick={() => addProductToOrder(product.id, product.price)}
                            />
                          ))}
                        </div>
                      )}
                      {search && (
                        <div className={classes.scrollable}>
                          <Typography variant="overline" component="p">
                            Productos
                          </Typography>
                          {allProducts
                            .filter(product => product.name.toLowerCase().includes(search.toLowerCase()) && product.enable === true)
                            .sort((a, b) => (a.name > b.name) ? 1 : ((b.name > a.name) ? -1 : 0))
                            .map(product => (
                              <Chip
                                key={product.id}
                                label={product.name}
                                className={classes.chip}
                                onClick={() => addProductToOrder(product.id, product.price)}
                              />
                            ))}
                        </div>
                      )}
                    </Grid>
                  ) : (
                    <Fragment>
                      <Grid item xs={12} lg={4}>
                        <Autocomplete
                          id="categories-list"
                          loading={loading}
                          loadingText="Cargando..."
                          options={categories}
                          onChange={(event, value) => handleSelectCategory(value)}
                          getOptionLabel={(option) => option.name}
                          style={{ width: '100%' }}
                          disableClearable={true}
                          fullWidth
                          renderInput={(params) => <TextField {...params} label="Categorias" variant="outlined" />}
                        />
                      </Grid>
                      {products.length > 0 && (
                        <Grid item xs={12} lg={6}>
                          <Autocomplete
                            id="products-list"
                            options={products}
                            onChange={(event, value) => setSelectedProduct(value)}
                            getOptionLabel={(option) => option?.name}
                            disableClearable={true}
                            fullWidth
                            style={{ width: '100%' }}
                            renderInput={(params) => <TextField {...params} label="Productos" variant="outlined" />}
                          />
                        </Grid>
                      )}
                      {products.length === 0 && selectedCategory && (
                        <Typography variant="body1" align="center">
                          No hay productos en esta categoría
                        </Typography>
                      )}
                      {selectedProduct && (
                        <Grid item xs={12} lg={2}>
                          <IconButton
                            aria-label="add"
                            onClick={() => {
                              addProductToOrder(selectedProduct.id, selectedProduct.price)
                              setSelectedProduct(undefined);
                              setSelectedCategory(undefined);
                            }}
                          >
                            <AddIcon fontSize="large" />
                          </IconButton>
                        </Grid>
                      )}
                    </Fragment>
                  )}
                </Grid>
              </Grid>
              <Grid item xs={12}>
                <TableContainer component={Paper}>
                  <Table className={classes.table} aria-label="simple table">
                    <TableHead>
                      <TableRow>
                        <TableCell>Producto</TableCell>
                        <TableCell align="right">Cantidad</TableCell>
                        <TableCell align="right">Precio</TableCell>
                        <TableCell align="right">Acciones</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {order.items.map(item => ({
                        product: getProduct(item.product),
                        quantity: item.quantity
                      })).map((row, index) => (
                        <TableRow key={index}>
                          <TableCell component="th" scope="row">
                            {row.product.name}
                          </TableCell>
                          <TableCell align="right">
                            <Grid container justifyContent="flex-end" alignItems="center">
                              <Grid item>
                                <IconButton
                                  aria-label="minus"
                                  size="small"
                                  disabled={row.quantity === 1}
                                  onClick={() => decreaseProduct(row.product.id, row.product.price)}
                                >
                                  <RemoveIcon />
                                </IconButton>
                              </Grid>
                              <Grid item>
                                {row.quantity}
                              </Grid>
                              <Grid item>
                                <IconButton
                                  aria-label="add"
                                  size="small"
                                  onClick={() => increaseProduct(row.product.id, row.product.price)}
                                >
                                  <AddIcon />
                                </IconButton>
                              </Grid>
                            </Grid>
                          </TableCell>
                          <TableCell align="right">${row.product.price}</TableCell>
                          <TableCell align="right">
                            <IconButton
                              aria-label="minus"
                              size="small"
                              onClick={() => deleteProduct(row.product.id, row.quantity, row.product.price)}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                      <TableRow>
                        <TableCell colSpan={4} align="right">Total: ${order.total + +order.extra}</TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
      <Grid item xs={11} lg={4}>
        <Grid container justifyContent="center">
          <Grid item>
            <Typography variant="h4" align="center">
              Datos de la venta
            </Typography>
          </Grid>
          <Grid item xs={11}>
            <FormControl component="fieldset">
              <FormLabel component="legend">Forma de pago</FormLabel>
              <RadioGroup
                aria-label="payment"
                name="payment"
                value={order.paymentMethod}
                onChange={handlePaymentChange}
                row
              >
                <FormControlLabel value="cash" control={<Radio />} label="Efectivo" />
                <FormControlLabel value="card" control={<Radio />} label="Tarjeta crédito/débito" />
              </RadioGroup>
            </FormControl>
          </Grid>
          {!order.delivery && (
            <Grid item xs={11}>
              <TextField
                fullWidth
                id="table"
                name="table"
                label="Mesa"
                variant="outlined"
                onChange={handleChangeTable}
                defaultValue={order.table}
                className={classes.inputsMargin}
              />
            </Grid>
          )}
          <Grid item xs={11}>
            <FormControl fullWidth className={classes.inputsMargin} variant="outlined">
              <InputLabel htmlFor="outlined-adornment-amount">Monto extra</InputLabel>
              <OutlinedInput
                id="extra"
                name="extra"
                onChange={handleChangeExtra}
                startAdornment={<InputAdornment position="start">$</InputAdornment>}
                labelWidth={100}
                defaultValue={order.extra}
              />
            </FormControl>
          </Grid>
          <Grid item xs={11}>
            <TextField
              id="notes"
              name="notes"
              label="Notas de la venta"
              multiline
              rows={4}
              placeholder="Escribe aquí alguna nota de la venta"
              variant="outlined"
              fullWidth
              onChange={handleNotesChange}
              defaultValue={order.notes}
              className={classes.inputsMargin}
            />
          </Grid>
          <Grid item xs={11}>
            <Grid container>
              <Grid item>
                <Button
                  variant="contained"
                  color="inherit"
                  startIcon={<ArrowBackIcon />}
                  onClick={() => history.goBack()}
                  className={classes.buttonsMargin}
                >
                  Regresar
                </Button>
              </Grid>
              <Grid item>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<SaveIcon />}
                  onClick={() => save('progress')}
                  className={classes.buttonsMargin}
                  disabled={
                    order.items.length === 0 ||
                    order.delivery ||
                    (!order.delivery && !order.table)
                  }
                >
                  Guardar orden
                </Button>
              </Grid>
              <Grid item>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<CheckCircleIcon />}
                  onClick={() => save('completed')}
                  className={classes.buttonsMargin}
                  disabled={order.items.length === 0 ||
                    (!order.delivery && !order.table)
                  }
                >
                  Terminar orden
                </Button>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  )
}